import { useCallback, useRef } from 'react';

/**
 * Foreground attention budget for a burst of notifications (plan §"Cấu hình, âm
 * thanh và chống xao nhãng"):
 *
 *  - rows of the same kind+classroom inside a 60 s window are surfaced as ONE
 *    aggregate that carries the real count, not one popup per row;
 *  - at most 3 popups are on screen at any moment, globally (not per key);
 *  - at most one sound plays per 10 s, globally.
 *
 * Popups have a KEYED lifecycle: `acceptToast` records which group owns each of
 * the open slots, and `closePopup(key)` releases exactly that group's slot. A
 * group that was refused while the budget was full is re-evaluated on every new
 * row (so the moment a slot frees, its next row opens the popup with the count
 * accumulated in the meantime) — a stored `shown:false` is never sticky.
 */

/** Rows of the same kind+classroom within this window share one aggregate. */
export const BURST_WINDOW_MS = 60_000;
/** At most this many popups may be on screen at once, across every key. */
export const MAX_CONCURRENT_POPUPS = 3;
/** At most one sound per this window, across every key. */
export const SOUND_COOLDOWN_MS = 10_000;

export interface BurstGroup {
  key: string;
  /** How many rows of this kind+classroom arrived inside the window. */
  count: number;
}

export interface BurstAcceptance {
  group: BurstGroup;
  /**
   * Whether a popup may be rendered for this group right now. A refused group
   * stays counted and is re-evaluated on its next row, so the value must be read
   * fresh each time rather than cached.
   */
  shown: boolean;
}

/** The popup key a group's toast must use, stable for the group's whole life. */
export function burstPopupKey(group: BurstGroup): string {
  return `notification-burst-${group.key}`;
}

function groupKey(item: { type: string; classroomCode?: string | null }): string {
  return `${item.type}:${item.classroomCode ?? ''}`;
}

export interface AttentionBurst {
  /**
   * Record an allowed toast for `item` and return its group plus whether a popup
   * may open now. `shown` is false when the global budget is full; the row is
   * still counted, and its next row re-evaluates the slot.
   */
  acceptToast(
    item: { type: string; classroomCode?: string | null },
    at: number,
  ): BurstAcceptance;
  /**
   * Release the slot held by `key`'s popup. Passing the key keeps the counter
   * tied to real DOM: a close for a group that owns no slot is a no-op, so a
   * stray or duplicated callback can never free another group's slot.
   */
  closePopup(key?: string): void;
  /** How many popups this tab currently holds open (drives the global cap). */
  openPopups(): number;
  /** Whether a sound may play now, consuming the global allowance when it may. */
  acceptSound(at: number): boolean;
}

export function createAttentionBurst(): AttentionBurst {
  const groups = new Map<string, { firstAt: number; count: number }>();
  /** Keys holding an open popup slot; size is the live DOM popup count. */
  const openKeys = new Set<string>();
  let lastSoundAt: number | null = null;

  const prune = (at: number) => {
    groups.forEach((entry, key) => {
      if (at - entry.firstAt >= BURST_WINDOW_MS) groups.delete(key);
    });
  };

  return {
    acceptToast(item, at) {
      prune(at);
      const key = groupKey(item);
      const seen = groups.get(key);
      const entry = seen && at - seen.firstAt < BURST_WINDOW_MS
        ? { firstAt: seen.firstAt, count: seen.count + 1 }
        : { firstAt: at, count: 1 };
      groups.set(key, entry);
      const group = { key, count: entry.count };

      // Already holds a slot: the provider updates that same popup in place, so
      // no new slot is taken and the row is still allowed to render.
      if (openKeys.has(key)) return { group, shown: true };
      // Budget full: stay counted, and re-evaluate on the next row. `shown` is
      // never persisted here, so a later row can open as soon as a slot frees.
      if (openKeys.size >= MAX_CONCURRENT_POPUPS) return { group, shown: false };
      openKeys.add(key);
      return { group, shown: true };
    },
    closePopup(key) {
      if (key == null) return;
      // Only the owning group's close frees a slot; everything else is a no-op.
      openKeys.delete(key);
    },
    openPopups() {
      return openKeys.size;
    },
    acceptSound(at) {
      if (lastSoundAt != null && at - lastSoundAt < SOUND_COOLDOWN_MS) return false;
      lastSoundAt = at;
      return true;
    },
  };
}

/**
 * React binding for the burst budget. The bookkeeping lives in a ref so a
 * re-render never resets a burst, and `closePopup` is stable for toast onClose.
 */
export function useAttentionBurst(): {
  acceptToast: AttentionBurst['acceptToast'];
  acceptSound: AttentionBurst['acceptSound'];
  closePopup: AttentionBurst['closePopup'];
  openPopups: AttentionBurst['openPopups'];
} {
  const burstRef = useRef<AttentionBurst>();
  if (!burstRef.current) burstRef.current = createAttentionBurst();
  const burst = burstRef.current;

  return {
    acceptToast: useCallback((item, at) => burst.acceptToast(item, at), [burst]),
    acceptSound: useCallback((at: number) => burst.acceptSound(at), [burst]),
    closePopup: useCallback((key?: string) => burst.closePopup(key), [burst]),
    openPopups: useCallback(() => burst.openPopups(), [burst]),
  };
}
